import React, { createContext, useContext, useState } from 'react';

// Network verification store — the mutable, cross-page state layer for the
// network-verification workflow (Priority 2). Everything else in the app reads
// static mock fixtures; this is where the practice's own additions live:
//
//   • plans         — plans added by the practice (CSV import, phone verification,
//                     manual entry). Provenance-tracked: who/when/source/evidence.
//   • verifications — append-only log of verification events. Confidence accrues
//                     from this (distinct patients confirmed, same plan + provider).
//   • thresholds    — per-practice confirmation count that flips a plan from
//                     low → high confidence (default 10, per the 3rd discussion).
//
// Persisted to localStorage so it survives reloads mid-demo. Mutators write
// synchronously (à la lib/auth.js) so the non-hook readers below stay current
// within the same render — that lets pure modules (lib/data.js) resolve network
// status against freshly-added plans without waiting for a React effect.

const LS_KEY = 'due_health_network';
export const DEFAULT_THRESHOLD = 10;

// Sources a plan/verification can originate from. Every entry is traceable —
// "the system never takes the fall" (see docs/BUSINESS_UNDERSTANDING.md).
export const SOURCES = {
  csv_import: 'CSV import',
  phone_call: 'Phone verification',
  manual: 'Manual entry',
  agent: 'Agent',
  '271_match': 'Eligibility match',
};

const EMPTY = { plans: [], verifications: [], thresholds: {} };

function read() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...EMPTY, ...JSON.parse(raw) };
  } catch (e) { /* ignore corrupt state */ }
  return { ...EMPTY };
}

function write(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
  return state;
}

/* --------------------------- pure helpers / keys --------------------------- */

// A stable grouping key for a plan. Unknown plans coming off a 271 may not have
// a planId in the mock catalog, so we key on payer + normalized plan name. This
// is what confidence is accrued against.
export function planKeyFor(payerId, planName) {
  return `${payerId || 'unknown'}::${String(planName || '').trim().toLowerCase()}`;
}

/* ----------------------- non-hook readers / selectors ---------------------- *
 * Mirror lib/features.js: these read localStorage directly so non-React
 * modules (e.g. lib/data.js detection + risk scoring) can use them. React
 * components should prefer the useNetwork() selectors, which track state. */

export function addedPlansFor(practiceId) {
  return read().plans.filter((p) => practiceId === 'all' || !practiceId || p.practiceId === practiceId);
}

export function verificationsFor(practiceId) {
  return read().verifications.filter((v) => practiceId === 'all' || !practiceId || v.practiceId === practiceId);
}

export function thresholdFor(practiceId) {
  const t = read().thresholds[practiceId];
  return typeof t === 'number' ? t : DEFAULT_THRESHOLD;
}

// Confidence for a plan: count DISTINCT patients with an in-network confirmation
// for this plan (optionally scoped to one provider — the 3rd-discussion rule is
// "same doctor, same plan, different patients"). At/above threshold → high
// confidence → detection stops routing it to the calling department.
export function confidenceFor({ planKey, practiceId, providerId = null }, state) {
  const all = state ? state.verifications : read().verifications;
  const matches = all.filter((v) =>
    v.planKey === planKey &&
    v.result === 'in_network' &&
    (practiceId == null || practiceId === 'all' || v.practiceId === practiceId) &&
    (providerId == null || v.providerId === providerId)
  );
  const distinctPatients = new Set(matches.map((v) => v.patientId).filter(Boolean));
  const count = distinctPatients.size || matches.length; // fall back to event count if patients unlabeled
  const threshold = state
    ? (typeof state.thresholds[practiceId] === 'number' ? state.thresholds[practiceId] : DEFAULT_THRESHOLD)
    : thresholdFor(practiceId);
  return {
    count,
    threshold,
    confirmations: matches.length,
    level: count >= threshold ? 'high' : 'low',
    ratio: threshold ? Math.min(1, count / threshold) : 0,
  };
}

/* -------------------------------- provider -------------------------------- */

const NetworkContext = createContext(null);

export function NetworkProvider({ children }) {
  const [state, setState] = useState(read);

  // Persist synchronously, then update React state — keeps localStorage and the
  // in-memory copy in lockstep for the non-hook readers above.
  const commit = (next) => { write(next); setState(next); return next; };

  // Add (or update) a plan in the practice's network library with full
  // provenance. Upserts by planKey within the practice: a plan appears once in
  // the library; repeated confirmations accrue in the verification log, not as
  // duplicate rows. The original addedBy/addedAt provenance is preserved.
  // record: { practiceId, providerId?, payerId, planId?, planName, planType?,
  //           status, source, addedBy, evidence? }
  const addPlan = (record) => {
    const cur = read();
    const key = planKeyFor(record.payerId, record.planName);
    const existing = cur.plans.find((p) => p.practiceId === record.practiceId && p.planKey === key);
    if (existing) {
      const plans = cur.plans.map((p) => (p === existing
        ? { ...p, status: record.status ?? p.status, evidence: record.evidence ?? p.evidence, lastVerifiedAt: new Date().toISOString() }
        : p));
      commit({ ...cur, plans });
      return plans.find((p) => p.practiceId === record.practiceId && p.planKey === key);
    }
    const plan = {
      id: `cp_add_${cur.plans.length + 1}_${Date.now()}`,
      providerId: null,
      planId: null,
      planType: null,
      status: 'unknown',
      evidence: null,
      addedAt: new Date().toISOString(),
      ...record,
      planKey: key,
    };
    commit({ ...cur, plans: [...cur.plans, plan] });
    return plan;
  };

  // Append a verification event to the audit log.
  // record: { practiceId, providerId?, payerId, planKey?, planName, patientId?,
  //           result, source, referenceNumber?, repName?, staffName?, date? }
  const logVerification = (record) => {
    const cur = read();
    const event = {
      id: `nv_${cur.verifications.length + 1}_${Date.now()}`,
      providerId: null,
      patientId: null,
      referenceNumber: null,
      repName: null,
      staffName: null,
      date: new Date().toISOString(),
      ...record,
      planKey: record.planKey || planKeyFor(record.payerId, record.planName),
    };
    commit({ ...cur, verifications: [...cur.verifications, event] });
    return event;
  };

  const setThreshold = (practiceId, n) => {
    const cur = read();
    commit({ ...cur, thresholds: { ...cur.thresholds, [practiceId]: n } });
  };

  // Clear all network state — useful for re-running a demo from a clean slate.
  const reset = () => commit({ ...EMPTY });

  const value = {
    plans: state.plans,
    verifications: state.verifications,
    thresholds: state.thresholds,
    addPlan,
    logVerification,
    setThreshold,
    reset,
    // selectors bound to live state (no localStorage round-trip on render)
    addedPlans: (practiceId) => state.plans.filter((p) => practiceId === 'all' || !practiceId || p.practiceId === practiceId),
    verificationLog: (practiceId) => state.verifications.filter((v) => practiceId === 'all' || !practiceId || v.practiceId === practiceId),
    thresholdFor: (practiceId) => (typeof state.thresholds[practiceId] === 'number' ? state.thresholds[practiceId] : DEFAULT_THRESHOLD),
    confidenceFor: (args) => confidenceFor(args, state),
  };

  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>;
}

export function useNetwork() {
  const ctx = useContext(NetworkContext);
  if (!ctx) throw new Error('useNetwork must be inside NetworkProvider');
  return ctx;
}
